import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useSnapshot } from 'valtio'

import {
  GetCountriesDocument,
  GetCountriesQuery,
  GetRgBannerDocument,
  GetRgBannerQuery,
  GetRgBannerQueryVariables,
  RgBanner,
} from '@app/graphql'
import { CurrentCountryState, CurrentUserState } from '@app/storage'

const ROTATION_SESSION_KEY = 'rgBannerMessageIndex'
const ROTATION_COUNTER_KEY = 'rgBannerSessionCount'

/**
 * Picks one message index per browser session, cycling through all messages across sessions
 * so every mandated message (e.g. the three DGOJ ones) is shown evenly.
 */
const sessionMessageIndex = (count: number): number => {
  if (count <= 1) return 0
  try {
    const stored = sessionStorage.getItem(ROTATION_SESSION_KEY)
    if (stored !== null) return Number(stored) % count

    const sessions = Number(localStorage.getItem(ROTATION_COUNTER_KEY) ?? '0') || 0
    localStorage.setItem(ROTATION_COUNTER_KEY, String(sessions + 1))
    sessionStorage.setItem(ROTATION_SESSION_KEY, String(sessions))
    return sessions % count
  } catch {
    return Math.floor(Math.random() * count)
  }
}

export interface ResolvedRgBanner {
  banner: RgBanner | null
  /** Messages to render — the single rotated one when the banner rotates. */
  messages: string[]
  loading: boolean
}

/**
 * Responsible-gambling banner for the user's country: profile country first, geo-IP otherwise.
 */
export function useRgBanner(): ResolvedRgBanner {
  const { countryId: profileCountryId } = useSnapshot(CurrentUserState)
  const { code: geoCountryCode, loaded: geoLoaded } = useSnapshot(CurrentCountryState)

  const { data: countriesData, loading: countriesLoading } = useQuery<GetCountriesQuery>(GetCountriesDocument, {
    skip: !profileCountryId,
    fetchPolicy: 'cache-first',
  })

  const profileCountryCode = useMemo(
    () => countriesData?.countries?.find(c => String(c.id) === String(profileCountryId))?.code,
    [ countriesData, profileCountryId ],
  )

  const countryCode = profileCountryCode || geoCountryCode || null
  const resolving = (!!profileCountryId && countriesLoading) || (!profileCountryCode && !geoLoaded)

  const { data, loading } = useQuery<GetRgBannerQuery, GetRgBannerQueryVariables>(GetRgBannerDocument, {
    variables: { countryCode },
    skip: resolving,
    // Admin edits can happen while the banner isn't mounted, so revalidate on every mount.
    fetchPolicy: 'cache-and-network',
  })

  const banner = data?.rgBanner ?? null

  const messages = useMemo(() => {
    if (!banner) return []
    if (!banner.rotateMessages) return banner.messages
    return [ banner.messages[sessionMessageIndex(banner.messages.length)] ]
  }, [ banner ])

  return { banner, messages, loading: resolving || loading }
}
